<template>
  <AppHeaderBack>Create Account</AppHeaderBack>
  <div class="min-h-screen bg-gray-100 pt-8 pb-8 px-4">
    <div class="max-w-3xl mx-auto">
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <!-- Auth Options -->
        <div v-if="!showEmailForm" class="space-y-4">
          <button
            @click="signInWithGoogleProvider"
            :disabled="loading"
            type="button"
            class="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200 disabled:opacity-50"
          >
            <svg class="w-5 h-5 mr-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </button>
          <button
            @click="showEmailForm = true"
            type="button"
            class="w-full flex justify-center items-center py-3 px-4 border border-primary rounded-lg shadow-sm text-lg font-medium text-primary bg-white hover:bg-primary-lighter focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200"
          >
            <span class="material-symbols-outlined text-xl mr-2">mail</span>
            Continue with Email
          </button>
        </div>
        <!-- Email Form -->
        <form v-else class="space-y-5" @submit.prevent="register">
          <div class="flex justify-between items-center mb-4">
            <span class="text-lg font-semibold text-gray-700">Sign up with Email</span>
            <button type="button" @click="showEmailForm = false" class="text-primary hover:underline text-sm">
              Cancel
            </button>
          </div>
          <!-- Email Input -->
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-1 text-left">Email</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                </svg>
              </div>
              <input
                id="email"
                v-model="email"
                type="email"
                required
                class="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 transition duration-200"
                placeholder="your@email.com"
              />
            </div>
          </div>
          <!-- Password Input -->
          <div>
            <label for="password" class="block text-sm font-medium text-gray-700 mb-1 text-left">Password</label>
            <div class="relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clip-rule="evenodd" />
                </svg>
              </div>
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                required
                class="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary placeholder-gray-400 transition duration-200"
                placeholder="••••••••"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button 
                  type="button" 
                  @click="showPassword = !showPassword"
                  class="text-gray-400 hover:text-gray-500 focus:outline-none"
                >
                  <span class="material-symbols-outlined text-xl">
                    {{ showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
            </div>
            <!-- Password Strength Indicator -->
            <div v-if="password.length > 0" class="mt-2">
              <div class="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
                <div 
                  class="h-full transition-all duration-300"
                  :class="{
                    'bg-red-500': passwordStrength === 0,
                    'bg-yellow-500': passwordStrength === 1,
                    'bg-green-500': passwordStrength >= 2
                  }"
                  :style="{ width: passwordStrength * 33.33 + '%' }"
                ></div>
              </div>
              <p class="text-xs mt-1 text-gray-500">
                Password strength: 
                <span :class="{
                  'text-red-500': passwordStrength === 0,
                  'text-yellow-500': passwordStrength === 1,
                  'text-green-500': passwordStrength >= 2
                }">
                  {{ passwordStrengthText }}
                </span>
              </p>
            </div>
          </div>
          <!-- Submit Button -->
          <button
            type="submit"
            :disabled="loading || passwordStrength < 0"
            class="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-lg font-medium text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200"
          >
            <svg v-if="loading" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ loading ? "Creating account..." : "Get Started" }}
            <svg v-if="!loading" xmlns="http://www.w3.org/2000/svg" class="ml-2 h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clip-rule="evenodd" />
            </svg>
          </button>
        </form>
        <div class="mt-6">
          <div class="relative">
            <div class="absolute inset-0 flex items-center">
              <div class="w-full border-t border-gray-300"></div>
            </div>
            <div class="relative flex justify-center text-sm">
              <span class="px-2 bg-white/80 text-gray-500">Already have an account?</span>
            </div>
          </div>
          <div class="mt-6">
            <router-link to="/login" class="w-full flex justify-center items-center py-3 px-4 border border-gray-300 rounded-lg shadow-sm text-lg font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition duration-200">
              Back to Login
              <span class="material-symbols-outlined ml-2">arrow_back</span>
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { registerUser, signInWithGoogle, handlePostAuthProfile } from '@/composables/useAuth';
import AppHeaderBack from '@/components/common/AppHeaderBack.vue';

export default {
  components: { AppHeaderBack },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      loading: false,
      showEmailForm: false
    };
  },
  computed: {
    passwordStrength() {
      if (this.password.length === 0) return -1;
      if (this.password.length < 6) return 0;
      if (this.password.length < 8) return 1;
      return 2;
    },
    passwordStrengthText() {
      switch(this.passwordStrength) {
        case -1: return 'Not entered';
        case 0: return 'Weak (too short)';
        case 1: return 'Medium';
        case 2: return 'Strong';
        default: return '';
      }
    }
  },
  methods: {
    async register() {
      if (this.passwordStrength < 1) {
        this.$store.dispatch('showAlert', { 
          message: "Please choose a stronger password (minimum 6 characters)", 
          type: "error" 
        });
        return;
      }

      this.loading = true;
      try {
        // Register with email/password
        const user = await registerUser(this.email, this.password);
        console.log('Email registration successful:', user);
        
        // Fetch and set the profile that was just created
        await this.$store.dispatch('fetchUserProfile');
        
        // Show success message
        this.$store.dispatch('showAlert', {
          message: 'Account created successfully!',
          type: 'success'
        });
        
        // Redirect to profile
        this.$router.push('/profile');
        
      } catch (error) {
        console.error('Registration error:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Registration failed',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
    async signInWithGoogleProvider() {
      this.loading = true;
      try {
        // Sign up with Google and get profile data (this creates new profile if first time)
        const result = await signInWithGoogle();
        console.log('Google sign-up result:', result);
        
        // Handle profile setup immediately for new users
        await handlePostAuthProfile(result);
        
        // Show success message
        this.$store.dispatch('showAlert', {
          message: 'Successfully signed up with Google!',
          type: 'success'
        });
        
        // Redirect to home or profile
        this.$router.push('/profile');
        
      } catch (error) {
        console.error('Google sign-up error:', error);
        this.$store.dispatch('showAlert', {
          message: error.message || 'Failed to sign up with Google',
          type: 'error'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Custom styling for password toggle buttons */
button[type="button"] {
  background: none;
  border: none;
  cursor: pointer;
  outline: none;
}
</style>
